import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BusService } from '../../../../services/bus.service';
import { BusWithDriverVM } from '../../../../models/BusModel';
import { ConfirmDeleteComponent } from '../../users/edit-user/confirm-delete/confirm-delete.component';
import { AuthService } from '../../../../services/auth.service';

const NONE_VALUE = 'none';

@Component({
  selector: 'app-my-bus',
  templateUrl: './my-bus.component.html',
  styleUrl: './my-bus.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MyBusComponent implements OnInit {
  readonly noneValue = NONE_VALUE;

  buses: BusWithDriverVM[] = [];
  selectedValue: string = NONE_VALUE;
  loading = true;
  saving = false;
  isDialog = false;

  constructor(
    private busService: BusService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    @Optional() public dialogRef?: MatDialogRef<MyBusComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: { isDialog?: boolean; mandatory?: boolean }
  ) {
    this.isDialog = !!(this.dialogRef && this.data && this.data.isDialog);
  }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.busService.getMyBusOptions().subscribe({
      next: buses => {
        this.buses = buses || [];
        this.selectedValue = this.buses.find(b => b.isCurrentUser)?.busId || NONE_VALUE;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Could not load buses.', 'Close', { duration: 4000 });
        this.cdr.detectChanges();
      }
    });
  }

  onSelect(busId: string): void {
    if (busId === this.selectedValue) return;

    const bus = busId === NONE_VALUE ? null : this.buses.find(b => b.busId === busId);
    const isTakenByOther = !!bus?.currentDriverName && !bus.isCurrentUser;

    if (isTakenByOther) {
      const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
        data: {
          message: `There is a driver (${bus.currentDriverName}) in this vehicle. Do you want to take over?`,
          buttonText: { ok: 'Yes', cancel: 'No' }
        }
      });

      dialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.selectedValue = busId;
          this.cdr.detectChanges();
          if (!this.isDialog) {
            this.save(busId);
          }
        }
      });
      return;
    }

    this.selectedValue = busId;
    this.cdr.detectChanges();

    if (!this.isDialog) {
      this.save(busId);
    }
  }

  onConfirm(): void {
    if (!this.isDialog) return;
    this.save(this.selectedValue);
  }

  private save(busId: string): void {
    this.saving = true;

    this.busService.setMyBus(busId === NONE_VALUE ? null : busId).subscribe({
      next: () => {
        this.saving = false;
        const userId = this.authService.getUserId() || 'user';
        sessionStorage.setItem(`shuttle_checkin_bus_selected_${userId}`, 'true');
        this.snackBar.open('Saved.', 'Close', { duration: 3000 });
        if (this.isDialog && this.dialogRef) {
          this.dialogRef.close(true);
        } else {
          this.load();
        }
      },
      error: () => {
        this.saving = false;
        this.snackBar.open('Could not save your selection.', 'Close', { duration: 4000 });
      }
    });
  }

  closeModal(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }
}
